import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTrainingModules, getModuleDetails, getDriverCompletions, submitQuizAttempt } from '../../services/trainingService';
import toast from 'react-hot-toast';
// ═══════════════ TRAINING MODULES LIST ═══════════════
export default function TrainingModules() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      getTrainingModules(),
      userProfile?.id ? getDriverCompletions(userProfile.id) : [],
    ]).then(([mods, comps]) => {
      setModules(mods);
      setCompletions(comps);
      setLoading(false);
    });
  }, [userProfile]);
  const getStatus = (moduleId) => {
    const comp = completions.find((c) => c.id === moduleId);
    if (!comp) return { label: 'Not started', color: '#94a3b8' };
    if (comp.status === 'overdue') return { label: 'Overdue', color: '#ff6b6b' };
    if (comp.status === 'expiring') return { label: 'Expiring soon', color: '#fbbf24' };
    return { label: 'Completed', color: '#2dd4a8' };
  };
  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  return (
    <div className="max-w-[600px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>
      <h1 className="font-serif mt-0 mb-2 text-brand-dark">Training</h1>
      <p className="text-body mb-6">Complete all modules to maintain your active driver status.</p>
      {modules.map((mod) => {
        const status = getStatus(mod.id);
        return (
          <div key={mod.id} onClick={() => navigate(`/driver/training/${mod.id}`)} className="p-5 rounded-2xl border border-slate-200 mb-3 cursor-pointer bg-white">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-brand-dark">{mod.order}. {mod.title}</span>
              <span className="px-2.5 py-1 rounded-full text-[0.75rem] font-semibold" style={{ color: status.color, background: `${status.color}15` }}>
                {status.label}
              </span>
            </div>
            <div className="text-[0.85rem] text-body">{mod.description}</div>
          </div>
        );
      })}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}
// ═══════════════ QUIZ PAGE ═══════════════
export function QuizPage() {
  const { moduleId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    getModuleDetails(moduleId).then((mod) => {
      setModule(mod);
      // Randomly select 10 questions
      const bank = mod?.quiz?.questionBank || [];
      const shuffled = [...bank].sort(() => Math.random() - 0.5).slice(0, 10);
      setQuestions(shuffled);
    });
  }, [moduleId]);
  const handleAnswer = (qId, optionIndex) => {
    setAnswers((prev) => ({ ...prev, [qId]: optionIndex }));
  };
  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Answer all questions'); return;
    }
    setSubmitting(true);
    try {
      const res = await submitQuizAttempt(userProfile.id, moduleId, answers);
      setResult(res);
      if (res.passed) toast.success(`Passed! Score: ${res.score}%`);
      else toast.error(`Failed. Score: ${res.score}%. Retake in 24hrs.`);
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };
  if (!module) return <div className="p-10 text-center">Loading...</div>;
  if (result) {
    return (
      <div className="max-w-[500px] mx-auto p-6 text-center">
        <div className="text-[4rem] mb-4">{result.passed ? '✅' : '❌'}</div>
        <h2 className="font-serif text-brand-dark">{result.passed ? 'Passed!' : 'Not Passed'}</h2>
        <p className="text-[1.5rem] font-bold" style={{ color: result.passed ? '#2dd4a8' : '#ff6b6b' }}>{result.score}%</p>
        <p className="text-body mb-6">
          {result.passed ? 'Module completed successfully.' : `You need 80% to pass. You can retake in 24 hours.`}
        </p>
        <button onClick={() => navigate('/driver/training')} className="btn btn-primary">Back to Training</button>
      </div>
    );
  }
  return (
    <div className="max-w-[600px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver/training')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Training</button>
      </div>
      <h1 className="font-serif mt-0 mb-1">{module.title} — Quiz</h1>
      <p className="text-body mb-6">Answer all 10 questions. 80% required to pass.</p>
      {questions.map((q, i) => (
        <div key={q.id} className="p-5 rounded-2xl border border-slate-200 mb-4">
          <div className="font-semibold mb-3 text-brand-dark">{i + 1}. {q.question}</div>
          {q.options.map((opt, oi) => (
            <label key={oi} onClick={() => handleAnswer(q.id, oi)} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] mb-1.5 cursor-pointer"
              style={{
                border: `1.5px solid ${answers[q.id] === oi ? '#2dd4a8' : '#e2e8f0'}`,
                background: answers[q.id] === oi ? 'rgba(45,212,168,0.04)' : '#fff',
              }}>
              <div className="w-5 h-5 rounded-full" style={{
                border: `2px solid ${answers[q.id] === oi ? '#2dd4a8' : '#cbd5e1'}`,
                background: answers[q.id] === oi ? '#2dd4a8' : 'transparent',
              }} />
              <span className="text-[0.9rem]">{opt}</span>
            </label>
          ))}
        </div>
      ))}
      <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary w-full">
        {submitting ? 'Submitting...' : `Submit (${Object.keys(answers).length}/10 answered)`}
      </button>
      <BackButtonBottom to="/driver/training" label="Training" />
    </div>
  );
}